import React, { useMemo, useState } from 'react';
import { NavLink as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Collapse, ListItem } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatBadge from 'src/layouts/DashboardLayout/NavBar/ChatBage';
import SetupAssistantBadge from 'src/layouts/DashboardLayout/NavBar/SetupAssistantBadge';
import ActivityBadge, { useActivityTotal } from 'src/layouts/DashboardLayout/NavBar/ActivityBadge';
import { ACTIVITY_AREA, ORDERS_NEW_FILTER_PARAM } from 'src/actions/activityActions';

import navItemStyle from 'src/layouts/DashboardLayout/NavBar/navItem.style';

const useStyles = makeStyles()(navItemStyle);

// CON-2026 — additionalOptions.badgeKey picks the icon's badge. 'chat' and
// 'setupAssistant' render their own badges unchanged (ChatBage owns the
// unread-count socket listener, so Connections behaves exactly as before);
// every activity area shares ActivityBadge.
const ACTIVITY_BADGE_KEYS = Object.values(ACTIVITY_AREA);

function NavItemBadge({ badgeKey, children }) {
  if (badgeKey === 'chat') {
    return (
      <ChatBadge className="navItemConnectionsBage">
        {children}
      </ChatBadge>
    );
  }
  if (badgeKey === 'setupAssistant') {
    return (
      <SetupAssistantBadge className="navItemConnectionsBage">
        {children}
      </SetupAssistantBadge>
    );
  }
  if (ACTIVITY_BADGE_KEYS.includes(badgeKey)) {
    return (
      <ActivityBadge area={badgeKey} className="navItemConnectionsBage">
        {children}
      </ActivityBadge>
    );
  }
  return children;
}

NavItemBadge.propTypes = {
  badgeKey: PropTypes.string,
  children: PropTypes.node,
};

function NavItem({
  title,
  href,
  depth,
  children,
  icon: Icon,
  className,
  open: openProp,
  info: Info,
  click,
  isPageBlocked,
  doesUserHaveSubscription,
  isShownWhenOtherHidden,
  additionalOptions,
  ...rest
}) {
  const [open, setOpen] = useState(openProp);
  const { classes, cx } = useStyles();
  const { badgeKey = null } = additionalOptions || {};
  // CON-2026 — with new orders waiting, Order Follow-Up opens on its
  // "Unseen (N)" filter.
  const newOrdersTotal = useActivityTotal(ACTIVITY_AREA.ORDERS);
  const linkHref = badgeKey === ACTIVITY_AREA.ORDERS && newOrdersTotal > 0
    ? `${href}?${ORDERS_NEW_FILTER_PARAM}=1`
    : href;

  // Sidebar Options that should be disabled when user has no subscription
  const subscriptionRequiredTitles = [
    // Settings items
    'Order Follow-Up Settings',
    'Events Settings',
    'Contact Settings',
    'Messaging Settings',
    'Message Templates',
    // View & Manage items
    'Order Follow-Up',
    'Events',
    'Contacts',
    'Connections',
    'Scheduled Connections',
    // Help & Information
    'Help & Information',
  ];
  const requiresSubscription = subscriptionRequiredTitles.includes(title);

  const isDisabled = requiresSubscription
    ? !doesUserHaveSubscription
    : (isPageBlocked && !isShownWhenOtherHidden);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    if (click) {
      click();
    }
  };

  const handleListItemClick = () => {
    if (click) {
      click();
    }
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  const listItemIcon = useMemo(() => {
    const IconComponent = Icon && (
      <Icon
        className={`${classes.icon} navItemIcon`}
        size="20"
      />
    );

    if (!badgeKey) {
      return IconComponent;
    }

    return (
      <NavItemBadge badgeKey={badgeKey}>
        {IconComponent}
      </NavItemBadge>
    );
  }, [badgeKey]);

  const listItemImageAndText = (
    <>
      {listItemIcon}
      <span className={`${classes.title} navItemText`}>
        {title}
      </span>
    </>
  );

  if (children) {
    return (
      <ListItem className={cx(classes.item, className)} disableGutters key={title} {...rest}>
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={style}
          disabled={isDisabled}
        >
          {listItemImageAndText}
          {open ? <ExpandLessIcon size="small" color="inherit" /> : <ExpandMoreIcon size="small" color="inherit" />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem className={cx(classes.itemLeaf, className)} disableGutters key={title} {...rest}>
      <Button
        activeClassName={classes.active}
        className={cx(classes.buttonLeaf, `depth-${depth}`)}
        component={RouterLink}
        exact
        style={style}
        to={linkHref}
        onClick={handleListItemClick}
        disabled={isDisabled}
      >
        {listItemImageAndText}
        {Info && <Info className={classes.info} />}
      </Button>
    </ListItem>
  );
}

NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  depth: PropTypes.number.isRequired,
  isPageBlocked: PropTypes.bool.isRequired,
  doesUserHaveSubscription: PropTypes.bool.isRequired,
  isShownWhenOtherHidden: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  href: PropTypes.string,
  icon: PropTypes.any,
  info: PropTypes.any,
  open: PropTypes.bool,
  click: PropTypes.any,
  additionalOptions: PropTypes.object,
};

NavItem.defaultProps = {
  open: false,
  isShownWhenOtherHidden: null,
  click: null,
  additionalOptions: null,
};

export default NavItem;
